/* Iter123k245 · Pagina "Branding Studio" accessibile anche ai tenant trial.
   Guida l'admin trial a caricare logo e firma al primo accesso con anteprima live del PDF. */
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import LogoFirmaCard from "../components/LogoFirmaCard";
import {
  ArrowLeft, FileText, RefreshCw, Loader2, CheckCircle2,
  Image as ImageIcon, PenLine, Sparkles,
} from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

/** Chiave localStorage per segnare che il prompt onboarding è stato visto. */
export const BRANDING_PROMPT_SEEN_KEY = "brandingPromptSeen_v1";

export default function BrandingStudio() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [assets, setAssets] = useState(null);
  // Chiave di refresh dell'iframe: ogni cambio forza il browser a ri-scaricare il PDF.
  const [previewKey, setPreviewKey] = useState(Date.now());
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const prevAssetsRef = useRef(null);

  useEffect(() => {
    // Segna il prompt come visto appena la pagina viene aperta.
    try { localStorage.setItem(BRANDING_PROMPT_SEEN_KEY, "1"); } catch { /* ignore */ }
  }, []);

  // Signature include un timestamp così anche la sostituzione (stesso URL ma file nuovo)
  // triggera il refresh. Il counter viene incrementato da ogni load() in LogoFirmaCard.
  useEffect(() => {
    if (!assets) return;
    const sig = `${assets.logo_url || ""}|${assets.firma_url || ""}|${assets.__v || 0}`;
    if (prevAssetsRef.current !== sig) {
      prevAssetsRef.current = sig;
      refreshPreview();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assets]);

  const refreshPreview = async () => {
    setPreviewLoading(true);
    try {
      // Richiede un download token monouso: così il PDF è scaricabile senza esporre il JWT nell'iframe.
      const { data } = await api.post("/download-token",
        { resource: "/api/me/tenant/branding/preview-pdf" });
      const url = `${BACKEND}/api/me/tenant/branding/preview-pdf?dt=${encodeURIComponent(data.dt)}&v=${Date.now()}`;
      setPreviewUrl(url);
      setPreviewKey(Date.now());
    } catch {
      toast.error("Impossibile generare l'anteprima PDF");
    } finally {
      setPreviewLoading(false);
    }
  };

  const logoReady = !!assets?.logo_url;
  const firmaReady = !!assets?.firma_url;
  const bothReady = logoReady && firmaReady;
  const isTrial = user?.account_type === "trial";
  // Studio Izzo (tenant_id="studio") usa il logo Ginius di default: niente nudging "senza logo".
  const isStudioIzzo = user?.tenant_id === "studio";

  return (
    <div className="max-w-6xl mx-auto p-4 sm:p-6" data-testid="branding-studio-page">
      <div className="flex items-center gap-3 mb-5">
        <Button variant="ghost" size="sm" onClick={() => navigate(-1)}
          className="shrink-0" data-testid="branding-back-btn">
          <ArrowLeft className="w-4 h-4 mr-1" /> Indietro
        </Button>
        <div>
          <h1 className="font-brand font-bold text-2xl sm:text-3xl text-slate-800">
            Branding Studio
          </h1>
          <p className="text-sm text-slate-600 mt-0.5">
            Carica il tuo logo e la firma grafica: appariranno subito nei PDF (preventivi, sinistri, Credius) e nelle intestazioni.
          </p>
        </div>
      </div>

      {/* Onboarding stepper: visibile fino a che entrambi non sono caricati · nascosto per Studio Izzo */}
      {!bothReady && !isStudioIzzo && (
        <Card data-testid="branding-onboarding-card"
          className="p-4 sm:p-5 mb-5 bg-gradient-to-br from-brand/5 to-amber-50 border-2 border-brand/20">
          <div className="flex items-start gap-3">
            <div className="shrink-0 rounded-xl p-2.5 bg-brand text-white">
              <Sparkles className="w-5 h-5" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="font-bold text-slate-900">
                {isTrial
                  ? "Benvenuto! Personalizza il tuo Studio in 2 passi"
                  : "Completa il tuo branding"}
              </div>
              <p className="text-sm text-slate-700 mt-1 leading-snug">
                Finché non carichi logo e firma, i PDF del tuo Studio verranno
                generati <b>senza logo</b>. Rilascia i file nelle aree qui sotto
                e guarda l&apos;anteprima aggiornarsi in diretta.
              </p>
              <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
                <StepBadge done={logoReady} icon={ImageIcon}
                  label="1. Logo dello Studio"
                  testid="step-logo" />
                <StepBadge done={firmaReady} icon={PenLine}
                  label="2. Firma grafica"
                  testid="step-firma" />
              </div>
            </div>
          </div>
        </Card>
      )}

      {bothReady && !isStudioIzzo && (
        <Card data-testid="branding-complete-card"
          className="p-4 sm:p-5 mb-5 bg-emerald-50 border-2 border-emerald-200">
          <div className="flex items-center gap-3">
            <CheckCircle2 className="w-6 h-6 text-emerald-600 shrink-0" />
            <div className="min-w-0">
              <div className="font-bold text-emerald-900">Branding completo</div>
              <div className="text-sm text-emerald-800 leading-snug">
                Logo e firma sono configurati: tutti i PDF del tuo Studio li utilizzeranno automaticamente.
              </div>
            </div>
          </div>
        </Card>
      )}

      {isStudioIzzo && (
        <Card data-testid="branding-studio-izzo-card"
          className="p-4 sm:p-5 mb-5 bg-slate-50 border-2 border-slate-200">
          <div className="flex items-center gap-3">
            <Sparkles className="w-6 h-6 text-brand shrink-0" />
            <div className="min-w-0">
              <div className="font-bold text-slate-800">Studio Izzo · Branding Ginius di default</div>
              <div className="text-sm text-slate-700 leading-snug">
                Il tuo tenant usa il logo e la firma Ginius di default sui PDF. Puoi comunque caricare
                asset personalizzati per sovrascriverli.
              </div>
            </div>
          </div>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-5">
        {/* Colonna sinistra: upload */}
        <div className="lg:col-span-3">
          <LogoFirmaCard onChange={setAssets} />
        </div>

        {/* Colonna destra: anteprima PDF live */}
        <div className="lg:col-span-2">
          <Card className="p-4 sm:p-5 sticky top-4" data-testid="branding-preview-card">
            <div className="flex items-center justify-between mb-3 gap-2">
              <div className="min-w-0">
                <h2 className="font-bold text-lg text-slate-800 flex items-center gap-2">
                  <FileText className="w-5 h-5 text-brand" /> Anteprima PDF
                </h2>
                <p className="text-xs text-slate-500 leading-snug mt-0.5">
                  Esempio di sottoscrizione preventivo con il tuo branding attuale.
                </p>
              </div>
              <Button variant="outline" size="sm" onClick={refreshPreview}
                disabled={previewLoading}
                data-testid="branding-preview-refresh"
                className="shrink-0 h-9">
                {previewLoading
                  ? <Loader2 className="w-4 h-4 animate-spin" />
                  : <RefreshCw className="w-4 h-4" />}
              </Button>
            </div>
            <div className="relative w-full rounded-lg border-2 border-slate-200 bg-slate-100 overflow-hidden"
              style={{ aspectRatio: "210 / 297" }}>
              {previewUrl ? (
                <iframe
                  key={previewKey}
                  src={previewUrl}
                  title="Anteprima PDF branding"
                  data-testid="branding-preview-iframe"
                  className="absolute inset-0 w-full h-full border-0"
                />
              ) : (
                <div className="absolute inset-0 flex items-center justify-center text-sm text-slate-500">
                  {previewLoading ? "Generazione anteprima…" : "Carica un asset per vedere l'anteprima"}
                </div>
              )}
            </div>
            <p className="text-[11px] text-slate-500 mt-2 leading-snug">
              L&apos;anteprima si aggiorna automaticamente ad ogni upload o rimozione. Il PDF contiene dati di
              esempio e viene usato solo per il controllo visivo.
            </p>
          </Card>
        </div>
      </div>
    </div>
  );
}

function StepBadge({ done, icon: Icon, label, testid }) {
  return (
    <div
      data-testid={testid}
      className={`rounded-lg px-3 py-2 flex items-center gap-2 border-2 ${
        done
          ? "bg-emerald-50 border-emerald-300 text-emerald-900"
          : "bg-white border-slate-200 text-slate-700"
      }`}
    >
      {done
        ? <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0" />
        : <Icon className="w-5 h-5 text-slate-400 shrink-0" />}
      <span className="font-semibold text-sm">{label}</span>
      <span className={`ml-auto text-[11px] font-bold px-2 py-0.5 rounded-full ${
        done ? "bg-emerald-200 text-emerald-900" : "bg-slate-200 text-slate-600"
      }`}>
        {done ? "FATTO" : "DA FARE"}
      </span>
    </div>
  );
}
